<template>
  <modal @close="$emit('close')" class-name="parcel-tariff-modal">
    <template slot="header">
      <h4 class="modal-title">{{ $t("parcelCalculator.dialog.title") }}</h4>
      <md-button class="md-simple md-just-icon md-round modal-default-button" @click="$emit('close')">
        <md-icon>clear</md-icon>
      </md-button>
    </template>

    <template slot="body">
      <div class="pt-toolbar">
        <div class="pt-carrier-switch">
          <button v-for="c in carrierKeys" :key="c" type="button" :class="['pt-carrier-tab', { 'pt-carrier-tab--active': carrier === c }]" @click="carrier = c">
            <img :src="'/img/logo-' + c.toLowerCase() + '.png'" :alt="c" />
            <span>{{ c }}</span>
          </button>
        </div>
      </div>

      <p v-if="!canEdit" class="pt-hint">{{ $t("parcelCalculator.dialog.readOnly") }}</p>

      <div v-if="loading" class="pt-hint">{{ $t("parcelCalculator.calculating") }}</div>
      <template v-else-if="state">
        <h5 class="pt-section">{{ $t("parcelCalculator.dialog.rates") }} {{ carrier }}</h5>
        <div v-if="isVertical" class="pt-grid-wrap">
          <table class="pt-grid pt-grid--vertical">
            <thead>
              <tr>
                <th>{{ $t("parcelCalculator.dialog.zone") }}</th>
                <th v-for="(step, i) in state[carrier].steps" :key="step.key" class="pt-weight">
                  <span class="pt-cell">
                    <span class="pt-unit">{{ $t("parcelCalculator.dialog.weightUpTo") }}</span>
                    <input v-model.number="step.weightUpTo" type="number" min="0.01" step="0.01" :disabled="!canEdit" />
                    <span class="pt-unit">kg</span>
                    <md-button v-if="canEdit" class="md-icon-button md-simple" :title="$t('parcelCalculator.dialog.removeStep')" @click="removeStep(i)">
                      <md-icon>delete</md-icon>
                    </md-button>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="z in zones" :key="z.zone">
                <th class="pt-zone" :title="z.zone">{{ z.countries.join(", ") }}</th>
                <td v-for="step in state[carrier].steps" :key="step.key">
                  <span class="pt-cell">
                    <input v-model.number="step.prices[z.zone]" type="number" min="0" step="0.01" :disabled="!canEdit" />
                    <span class="pt-unit">EUR</span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="pt-grid-wrap">
          <table class="pt-grid">
            <thead>
              <tr>
                <th class="pt-weight">{{ $t("parcelCalculator.dialog.weightUpTo") }}</th>
                <th v-for="z in zones" :key="z.zone" :title="z.zone">{{ z.countries.join(", ") }}</th>
                <th v-if="canEdit" class="pt-remove"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(step, i) in state[carrier].steps" :key="step.key">
                <td class="pt-weight">
                  <span class="pt-cell">
                    <input v-model.number="step.weightUpTo" type="number" min="0.01" step="0.01" :disabled="!canEdit" />
                    <span class="pt-unit">kg</span>
                  </span>
                </td>
                <td v-for="z in zones" :key="z.zone">
                  <span class="pt-cell">
                    <input v-model.number="step.prices[z.zone]" type="number" min="0" step="0.01" :disabled="!canEdit" />
                  </span>
                </td>
                <td v-if="canEdit" class="pt-remove">
                  <md-button class="md-icon-button md-simple" :title="$t('parcelCalculator.dialog.removeStep')" @click="removeStep(i)">
                    <md-icon>delete</md-icon>
                  </md-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pt-grid-foot">
          <md-button v-if="canEdit" class="md-simple" @click="addStep"><md-icon>add</md-icon> {{ $t("parcelCalculator.dialog.addStep") }}</md-button>
          <span class="pt-hint">{{ $t("parcelCalculator.dialog.emptyHint") }} (EUR)</span>
        </div>

        <h5 class="pt-section">{{ $t("parcelCalculator.dialog.fees") }} {{ carrier }}</h5>
        <div class="pt-fees">
          <div v-for="key in feeKeys[carrier]" :key="key" class="pt-fee">
            <label>{{ $t("parcelCalculator.rows." + key) }}</label>
            <span class="pt-cell">
              <input v-model.number="state[carrier].fees[key]" type="number" min="0" step="0.01" :disabled="!canEdit" />
              <span class="pt-unit">{{ key === "Energy" ? "%" : "EUR" }}</span>
            </span>
          </div>
        </div>
      </template>
      <p v-if="error" class="pt-error">{{ error }}</p>
    </template>

    <template slot="footer">
      <md-button class="md-secondary mr-1" @click="$emit('close')">{{ $t("parcelCalculator.dialog.close") }}</md-button>
      <md-button v-if="canEdit" class="md-success" :disabled="saving || loading || !state" @click="save">{{ $t("parcelCalculator.dialog.save") }}</md-button>
    </template>
  </modal>
</template>

<script>
import { Modal } from "@/components";
import parcelService from "@/services/parcel.service";

const CARRIERS = ["DHL", "DPD"];
const FEE_KEYS = {
  DHL: ["Toll", "Energy", "Bulky", "GoGreen", "Peak"],
  DPD: ["Toll", "Energy", "NcSurcharge", "NcVolume", "Oversize", "Freight", "Security", "Peak"],
};

let stepCounter = 0;

export default {
  components: { Modal },
  props: {
    initialYear: { type: Number, default: null },
    zones: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      carrierKeys: CARRIERS,
      feeKeys: FEE_KEYS,
      carrier: "DHL",
      year: this.initialYear,
      state: null,
      loading: false,
      saving: false,
      error: "",
    };
  },
  computed: {
    isVertical() {
      return !!this.state && this.state[this.carrier].steps.length <= 3;
    },
  },
  created() {
    if (this.year != null) this.loadYear(this.year);
  },
  methods: {
    errorText(err) {
      const reason = (err && err.reason) || (err && err.response && err.response.status === 403 ? "forbidden" : "generic");
      const key = `parcelCalculator.errors.${reason}`;
      return this.$te(key) ? this.$t(key) : this.$t("parcelCalculator.errors.generic");
    },
    buildState(model) {
      const state = {};
      CARRIERS.forEach((carrier) => {
        const byWeight = new Map();
        (model.rates || [])
          .filter((r) => r.carrier === carrier)
          .forEach((r) => {
            if (!byWeight.has(r.weightUpTo)) byWeight.set(r.weightUpTo, {});
            byWeight.get(r.weightUpTo)[r.zone] = r.price;
          });
        const steps = [...byWeight.entries()].sort((a, b) => a[0] - b[0]).map(([weightUpTo, prices]) => this.newStep(weightUpTo, prices));
        const fees = {};
        FEE_KEYS[carrier].forEach((key) => {
          const fee = (model.fees || []).find((f) => f.carrier === carrier && f.key === key);
          fees[key] = fee ? fee.value : 0;
        });
        state[carrier] = { steps, fees };
      });
      return state;
    },
    newStep(weightUpTo, prices = {}) {
      const full = {};
      this.zones.forEach((z) => {
        full[z.zone] = prices[z.zone] != null ? prices[z.zone] : "";
      });
      return { key: ++stepCounter, weightUpTo, prices: full };
    },
    buildPayload() {
      const rates = [];
      const fees = [];
      CARRIERS.forEach((carrier) => {
        this.state[carrier].steps.forEach((step) => {
          this.zones.forEach((z) => {
            const price = step.prices[z.zone];
            if (price !== "" && price != null) {
              rates.push({ carrier, weightUpTo: step.weightUpTo, zone: z.zone, price });
            }
          });
        });
        FEE_KEYS[carrier].forEach((key) => {
          const value = this.state[carrier].fees[key];
          fees.push({ carrier, key, value: value === "" || value == null ? 0 : value });
        });
      });
      return { year: this.year, rates, fees };
    },
    loadYear(year) {
      if (year == null) return;
      this.loading = true;
      this.error = "";
      return parcelService
        .getTariffs(year)
        .then((model) => {
          this.state = this.buildState(model);
        })
        .catch((err) => {
          this.state = null;
          this.error = err && err.response && err.response.status === 404 ? this.$t("parcelCalculator.errors.no_tariff_year") : this.$t("parcelCalculator.dialog.loadFailed");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addStep() {
      const steps = this.state[this.carrier].steps;
      const last = steps.length ? steps[steps.length - 1].weightUpTo : 0;
      steps.push(this.newStep(Math.round((Number(last) + 10) * 100) / 100));
    },
    removeStep(index) {
      this.state[this.carrier].steps.splice(index, 1);
    },
    save() {
      this.saving = true;
      this.error = "";
      parcelService
        .saveTariffs(this.year, this.buildPayload())
        .then((model) => {
          this.state = this.buildState(model);
          this.notify("parcelCalculator.dialog.saved", "success");
          this.$emit("changed", this.year);
        })
        .catch((err) => {
          this.error = this.errorText(err);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    notify(messageKey, type) {
      this.$notify({ timeout: 2500, message: this.$t(messageKey), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type });
    },
  },
};
</script>

<style lang="scss">
.parcel-tariff-modal {
  width: 1000px;
  max-width: 96vw !important;
  text-align: left;

  .modal-body {
    height: min(940px, calc(100vh - 190px));
    overflow-y: auto;
  }
  .pt-toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  .pt-carrier-switch {
    margin-left: auto;
  }
  .pt-section {
    margin: 18px 0 8px;
    font-weight: 500;
  }
  .pt-hint {
    color: #777;
    font-size: 12px;
    margin: 6px 0;
  }
  .pt-error {
    color: #f44336;
    margin-top: 10px;
  }
  .pt-grid-wrap {
    overflow-x: auto;
  }
  .pt-grid {
    border-collapse: collapse;
    width: 100%;
    th {
      font-size: 12px;
      font-weight: 500;
      padding: 4px 4px;
      text-align: left;
      white-space: nowrap;
    }
    td {
      padding: 2px 3px;
    }
    .pt-weight {
      min-width: 110px;
    }
    .pt-remove {
      width: 44px;
    }
  }
  .pt-cell {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    input {
      width: 66px;
      padding: 3px 4px;
      border: 1px solid #ddd;
      border-radius: 3px;
      text-align: right;
      &:disabled {
        background: #f6f6f6;
        color: #555;
      }
    }
  }
  .pt-weight .pt-cell input {
    width: 76px;
  }
  .pt-unit {
    color: #777;
    font-size: 12px;
  }
  .pt-fees {
    display: flex;
    flex-direction: column;
    max-width: 420px;
  }
  .pt-fee {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    label {
      font-size: 14px;
      color: #444;
      margin: 0;
    }
    .pt-cell input {
      width: 90px;
    }
    .pt-unit {
      display: inline-block;
      width: 30px;
    }
  }
  .pt-grid-foot {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
  }
  .pt-grid--vertical {
    max-width: 520px;
    th.pt-zone {
      font-size: 14px;
      font-weight: 400;
      color: #444;
      padding: 4px 16px 4px 0;
      border-bottom: 1px solid #eee;
    }
    td {
      border-bottom: 1px solid #eee;
      padding: 4px 3px;
    }
  }
  .pt-carrier-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: 8px;
    padding: 4px 14px 4px 8px;
    border: 2px solid transparent;
    border-radius: 6px;
    background: #f3f3f3;
    cursor: pointer;
    color: #555;
    img {
      height: 28px;
      width: auto;
      border-radius: 0 !important;
    }
    &--active {
      border-color: #7f915a;
      background: #f1f5e8;
      font-weight: 500;
    }
  }
}
</style>
